import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { DateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { FilterChip, PaginationConfig, TableColumn } from '@shared/components/data-table/data-table.component';
import { DateRangePickerComponent } from '@shared/components/date-range-picker/date-range-picker.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { formatUtcDateTimeForUser } from '@shared/utils/utc-date.util';
import { PaginatedUserActivityResponse, UserActivityItem, UserBasicDetails, UserService } from '../../services/user.service';
import { ADMIN_ACTIVITY_USER_ID } from '../../models/admin-user.constants';

@Component({
  selector: 'app-user-activity',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    TableSurfaceComponent,
    DateRangePickerComponent
  ],
  templateUrl: './user-activity.component.html',
  styleUrl: './user-activity.component.scss'
})
export class UserActivityComponent implements OnInit, OnDestroy {
  readonly columns: TableColumn[] = [
    { key: 'timestamp', header: 'Fecha y hora', width: '18%', hideable: true },
    { key: 'actionType', header: 'Accion', width: '18%', hideable: true },
    { key: 'entity', header: 'Entidad', width: '16%', hideable: true },
    { key: 'details', header: 'Detalle', hideable: true },
    { key: 'result', header: 'Resultado', width: '14%', hideable: true },
  ];

  readonly actionTypes = [
    { value: '', label: 'Todas' },
    { value: 'LOGIN', label: 'Inicio de sesion' },
    { value: 'CREATE', label: 'Creacion' },
    { value: 'UPDATE', label: 'Edicion' },
    { value: 'DELETE', label: 'Eliminacion' },
    { value: 'STATUS_CHANGE', label: 'Cambio de estado' },
    { value: 'ROLE_ASSIGNMENT_UPDATE', label: 'Roles actualizados' },
    { value: 'TRANSFER_RESOLVE', label: 'Transferencia resuelta' },
  ];

  readonly filterForm = new FormGroup({
    actionType: new FormControl('', { nonNullable: true }),
  });

  readonly dateRangeGroup = new FormGroup({
    start: new FormControl<any | null>(null),
    end: new FormControl<any | null>(null),
  });

  user: UserBasicDetails | null = null;
  tableData: UserActivityItem[] = [];
  isLoading = false;
  errorMessage = '';
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  private readonly destroy$ = new Subject<void>();
  private userId = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id') ?? '';
    this.loadUser();
    this.loadActivity();
    this.setupFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get paginationConfig(): PaginationConfig {
    return {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalCount: this.totalCount,
      totalPages: this.totalPages
    };
  }

  get pageTitle(): string {
    return `Actividad de ${this.user?.username || 'usuario'}`;
  }

  get activeFilterChips(): FilterChip[] {
    const chips: FilterChip[] = [];
    const actionType = this.filterForm.controls.actionType.value;
    const from = this.dateRangeGroup.controls.start.value;
    const to = this.dateRangeGroup.controls.end.value;

    if (actionType) {
      chips.push({
        key: 'actionType',
        label: `Accion: ${this.getActionLabel(actionType)}`,
        removable: true
      });
    }

    if (from) {
      chips.push({
        key: 'dateRange',
        label: `Fecha: ${this.formatDateOnly(from)} - ${to ? this.formatDateOnly(to) : '...'}`,
        removable: true
      });
    } else if (to) {
      chips.push({
        key: 'dateRange',
        label: `Fecha: ... - ${this.formatDateOnly(to)}`,
        removable: true
      });
    }

    return chips;
  }

  goBack(): void {
    this.router.navigate(this.userId === ADMIN_ACTIVITY_USER_ID ? ['/users'] : ['/users', this.userId]);
  }

  onFilterRemove(key: string): void {
    if (key === 'actionType') {
      this.filterForm.controls.actionType.setValue('');
    }
    if (key === 'dateRange') {
      this.dateRangeGroup.reset({ start: null, end: null });
    }
  }

  onPageChange(event: { page: number }): void {
    this.currentPage = event.page;
    this.loadActivity();
  }

  onPageSizeChange(event: { pageSize: number }): void {
    this.pageSize = event.pageSize;
    this.currentPage = 1;
    this.loadActivity();
  }

  formatDateTime(value?: string | null): string {
    return formatUtcDateTimeForUser(value);
  }

  getActionLabel(actionType?: string | null): string {
    return this.actionTypes.find(x => x.value === actionType)?.label ?? actionType ?? '-';
  }

  private setupFilters(): void {
    this.filterForm.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged((previous, current) => previous.actionType === current.actionType),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.loadActivity();
      });

    this.dateRangeGroup.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged((previous, current) =>
          this.sameDate(previous.start, current.start) &&
          this.sameDate(previous.end, current.end)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.loadActivity();
      });
  }

  private loadUser(): void {
    this.userService.getUserById(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: user => {
          this.user = user;
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'No se pudo cargar el usuario.';
          this.cdr.markForCheck();
        }
      });
  }

  private loadActivity(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();

    const from = this.toIsoDateStart(this.dateRangeGroup.controls.start.value);
    const to = this.toIsoDateEnd(this.dateRangeGroup.controls.end.value);

    this.userService.getUserActivity(this.userId, {
      page: this.currentPage,
      pageSize: this.pageSize,
      actionType: this.filterForm.controls.actionType.value || null,
      from,
      to
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => this.handleLoadSuccess(response),
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'No se pudo cargar la actividad.';
          this.tableData = [];
          this.totalCount = 0;
          this.totalPages = 0;
          this.cdr.markForCheck();
        }
      });
  }

  private handleLoadSuccess(response: PaginatedUserActivityResponse): void {
    this.tableData = response.items ?? [];
    this.currentPage = response.pageNumber || this.currentPage;
    this.pageSize = response.pageSize || this.pageSize;
    this.totalCount = response.totalCount || 0;
    this.totalPages = response.totalPages || Math.ceil(this.totalCount / this.pageSize);
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private toIsoDateStart(value: any | null): string | null {
    if (!value) {
      return null;
    }

    const date = this.toDateParts(value);
    return new Date(date.year, date.month, date.day, 0, 0, 0, 0).toISOString();
  }

  private toIsoDateEnd(value: any | null): string | null {
    if (!value) {
      return null;
    }

    const date = this.toDateParts(value);
    return new Date(date.year, date.month, date.day, 23, 59, 59, 999).toISOString();
  }

  private formatDateOnly(value: any): string {
    const date = this.toDateParts(value);

    return new Intl.DateTimeFormat('es-AR', {
      dateStyle: 'short'
    }).format(new Date(date.year, date.month, date.day));
  }

  private sameDate(first?: any | null, second?: any | null): boolean {
    if (!first || !second) {
      return !first && !second;
    }

    const firstDate = this.toDateParts(first);
    const secondDate = this.toDateParts(second);
    return firstDate.year === secondDate.year &&
      firstDate.month === secondDate.month &&
      firstDate.day === secondDate.day;
  }

  private toDateParts(value: any): { year: number; month: number; day: number } {
    return {
      year: this.dateAdapter.getYear(value),
      month: this.dateAdapter.getMonth(value),
      day: this.dateAdapter.getDate(value),
    };
  }
}
